import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Play } from 'lucide-react'
import type { AgentSession, Ok } from '@one-deployment/api'
import { Button } from '@/components/ui'
import { del, enc, post } from '@/lib/api'

/**
 * Whether a session's run panel is open, per browser. The dev server itself
 * lives on the box (tmux `<session>-run`), so it keeps running with the panel
 * closed or the tab gone; this only remembers to show it again.
 */
export function useRunOpen(sessionId: string | undefined): [boolean, (v: boolean) => void] {
  const k = sessionId ? `trisof.run.${sessionId}` : null
  const [open, setOpen] = useState(() => !!k && localStorage.getItem(k) === '1')
  useEffect(() => setOpen(!!k && localStorage.getItem(k) === '1'), [k])
  return [open, (v) => { if (k) { if (v) localStorage.setItem(k, '1'); else localStorage.removeItem(k) } setOpen(v) }]
}

export function useRun(session: AgentSession | undefined) {
  const start = useMutation({ mutationFn: () => post<Ok>(`/api/sessions/${enc(session!.id)}/run`, {}) })
  const stop = useMutation({ mutationFn: () => del<Ok>(`/api/sessions/${enc(session!.id)}/run`) })
  const help = useMutation({ mutationFn: () => post<Ok>(`/api/sessions/${enc(session!.id)}/run/help`, {}) })
  return { start, stop, help }
}

export function RunButton({ session, onOpen }: { session: AgentSession; onOpen: () => void }) {
  const { start } = useRun(session)
  return (
    <>
      {start.error && <span className="text-err">{(start.error as Error).message}</span>}
      <Button size="sm" className="h-5 px-2" disabled={start.isPending} title="Start the dev server and show it here"
        onClick={() => start.mutate(undefined, { onSuccess: onOpen })}><Play size={12} /> {start.isPending ? 'Starting…' : 'Run'}</Button>
    </>
  )
}
